/**
 * KnowledgeBasePage - global knowledge base view.
 * Route: /dashboard/knowledge-bases/:kbId.
 * Unknown id -> redirect to the first KB.
 */
import { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import { Page, Content } from '@backstage/core-components';
import { KNOWLEDGE_BASES, getKnowledgeBase } from '../dashboard/registry';
import { useTabStyles } from '../sampleProject/tabStyles';
import { useSetTopBarTitle } from '../shell/TopBarTitleContext';
import { useTranslation } from 'react-i18next';
import {
  UploadDocumentsDialog,
  type UploadedSummary,
} from './UploadDocumentsDialog';

const usePageStyles = makeStyles(theme => ({
  page: { height: '100%', display: 'flex', flexDirection: 'column' },
  content: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    gap: theme.spacing(2),
    maxWidth: 1100,
    width: '100%',
  },
  header: { flexShrink: 0 },
  title: { margin: 0, fontSize: '1.6rem', color: theme.palette.text.primary },
  desc: {
    margin: '6px 0 0',
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
  },
}));

export function KnowledgeBasePage() {
  const p = usePageStyles();
  const c = useTabStyles();
  const { t } = useTranslation('knowledgeBase');
  const { t: projectT } = useTranslation('projects');
  const { kbId } = useParams();
  const kb = getKnowledgeBase(kbId);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploaded, setUploaded] = useState<UploadedSummary | null>(null);

  // Set the topbar title/subtitle for this page.
  useSetTopBarTitle(
    kb ? projectT(kb.name) : '',
    kb ? projectT(kb.description) : '',
  );

  if (!kb) {
    return (
      <Navigate
        to={`/dashboard/knowledge-bases/${KNOWLEDGE_BASES[0].id}`}
        replace
      />
    );
  }

  return (
    <Page themeId="tool" className={p.page}>
      <Content className={p.content}>
        <div className={p.header}>
          <h1 className={p.title}>{projectT(kb.name)}</h1>
          <p className={p.desc}>{projectT(kb.description)}</p>
        </div>

        <div className={c.scroll}>
          <div className={c.toolbar}>
            <div>
              <div className={c.toolbarTitle}>{t('documents')}</div>
              <div className={c.toolbarSub}>
                {t('documentCount', { count: kb.documents.length })}
              </div>
            </div>
            <button
              type="button"
              className={c.primaryBtn}
              onClick={() => setUploadOpen(true)}
            >
              + {t('upload')}
            </button>
          </div>

          {kb.documents.length === 0 ? (
            <div className={c.empty}>{t('empty')}</div>
          ) : (
            <div className={c.list}>
              {kb.documents.map(doc => (
                <Paper key={doc.id} elevation={1} className={c.card}>
                  <div className={c.cardMain}>
                    <div className={c.cardTitle}>{doc.name}</div>
                    <div className={c.cardSub}>
                      {doc.size} · {t('updated', { date: doc.updated })}
                    </div>
                  </div>
                  <div className={c.cardMeta}>
                    <span className={`${c.badge} ${c.badgeInfo}`}>
                      {doc.type}
                    </span>
                  </div>
                </Paper>
              ))}
            </div>
          )}
        </div>
      </Content>
      <UploadDocumentsDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={setUploaded}
      />
      <Snackbar
        open={uploaded !== null}
        autoHideDuration={6000}
        onClose={() => setUploaded(null)}
      >
        <Alert severity="success" onClose={() => setUploaded(null)}>
          {uploaded &&
            t('uploadDialog.success', {
              count: uploaded.count,
              project: uploaded.projectName,
            })}
        </Alert>
      </Snackbar>
    </Page>
  );
}
